/**
 * Civil-date arithmetic for the daily and weekly forge logic (crafting.md §2.2, §2.5). The caller
 * passes its LOCAL date as 'YYYY-MM-DD' (the web formats `new Date()`); progression never reads
 * a clock. Pure integer maths (Howard Hinnant's days_from_civil), no `Date`.
 */
import { FAVORED_SLOT_ORDER } from './forge.constants';
import type { ItemSlot } from './types';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function daysInMonth(y: number, m: number): number {
  if (m === 2) return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28;
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
}

/** True for a well-formed, real calendar date 'YYYY-MM-DD'. */
export function isLocalDate(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  const m = DATE_RE.exec(s);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  return mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo);
}

/** Days since 1970-01-01 of a 'YYYY-MM-DD' date. Throws on a malformed date. */
export function dayNumber(date: string): number {
  if (!isLocalDate(date)) throw new Error(`bad local date '${date}' (want YYYY-MM-DD)`);
  const [ys, ms, ds] = date.split('-');
  let y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  y -= m <= 2 ? 1 : 0;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

/** Inverse of dayNumber. */
export function dateFromDay(day: number): string {
  const z = day + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  const y = yoe + era * 400 + (m <= 2 ? 1 : 0);
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** ISO weekday 1 (Monday) .. 7 (Sunday). 1970-01-01 was a Thursday. */
export function isoWeekday(day: number): number {
  return ((((day + 3) % 7) + 7) % 7) + 1;
}

/** ISO week id 'YYYY-Www' of a day number (weeks start Monday; week 1 holds the first Thursday). */
export function isoWeek(day: number): string {
  const thursday = day - isoWeekday(day) + 4;
  const year = Number(dateFromDay(thursday).slice(0, 4));
  const jan1 = dayNumber(`${String(year).padStart(4, '0')}-01-01`);
  const week = Math.floor((thursday - jan1) / 7) + 1;
  return `${String(year).padStart(4, '0')}-W${String(week).padStart(2, '0')}`;
}

/** Favored Slot of the Day: +FAVORED_LUCK on crafts in this slot all day. */
export function favoredSlot(day: number): ItemSlot {
  const h = Math.imul(day | 0, 2654435761 | 0) >>> 0;
  return FAVORED_SLOT_ORDER[h % FAVORED_SLOT_ORDER.length]!;
}

/** Favored slot for a local date string. */
export function favoredSlotFor(date: string): ItemSlot {
  return favoredSlot(dayNumber(date));
}
